"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ClearPathLendingLogo } from "@assets/icons";

import { usePathname } from "next/navigation";

function Aside() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [isMenuOpenMobile, setIsMenuOpenMobile] = useState(false);
    const pathname = usePathname();

    const menuItems = [
        { href: "/about", label: "About Us", icon: "fa-building" },
        { href: "/loan-types", label: "Loan Types", icon: "fa-percent" },
        { href: "/refinance", label: "Refinance", icon: "fa-repeat" },
        { href: "/purchase", label: "Purchase", icon: "fa-shopping-cart" },
        { href: "/calculators-home-tools", label: "Home Tools", icon: "fa-calculator" },
        { href: "/loyalty", label: "Loyalty", icon: "fa-star" },
        { href: "/careers", label: "Careers", icon: "fa-user" },
    ];

    return (
        <aside
            className={`aside-banner-offset bg-bgWhite lg:shadow-menu transition-all duration-300 z-50 ${
                menuOpen ? "overflow-y-auto h-screen" : ""
            } lg:flex lg:flex-col lg:w-asideWidth lg:fixed lg:left-0`}
        >
            {/* Mobile Header */}
            <header className="block lg:hidden relative w-full bg-white z-50">
                <div className="flex items-center justify-between px-6 py-4">
                    {/* Mobile Menu Button */}
                    <button
                        type="button"
                        aria-label="Toggle menu"
                        aria-expanded={isMenuOpenMobile}
                        aria-controls="mobile-menu"
                        className="block text-xs text-midGrey focus:outline-none md:mb-1 md:ml-[59px]"
                        onClick={() => setIsMenuOpenMobile((v) => !v)}
                    >
                        <div className="flex items-center justify-center w-3 h-[14px] text-silver">
                            <i aria-hidden="true" className="fa fa-bars leading-none !text-sm"></i>
                        </div>
                    </button>

                    {/* Logo */}
                    <div className="flex justify-center items-center w-full md:pr-[90px]">
                        <Link
                            href="https://www.clearpathlending.com"
                            aria-label="ClearPath Lending"
                            aria-current={pathname === "/" ? "page" : undefined}
                        >
                            <Image
                                src={ClearPathLendingLogo}
                                alt="ClearPath Lending Logo"
                                width={200}
                                height={72}
                                className="max-h-20"
                                priority
                            />
                        </Link>
                    </div>
                </div>

                {/* Mobile Menu */}
                <nav id="mobile-menu" className="bg-white" aria-label="Mobile" hidden={!isMenuOpenMobile}>
                    <ul className="list-none pl-6 p-4 pt-0">
                        {menuItems.map((item, index) => (
                            <li
                                key={item.href}
                                className={`border-b border-lightgray ${index === menuItems.length - 1 ? "border-none" : ""}`}
                            >
                                <Link
                                    href={item.href}
                                    className={`flex items-center text-xs py-3 transition ${
                                        pathname === item.href ? "text-blue" : "text-midGrey hover:text-blue"
                                    }`}
                                    aria-current={pathname === item.href ? "page" : undefined}
                                >
                                    <i className={`fa ${item.icon} mr-3`} aria-hidden="true"></i>
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>
            </header>

            {/* Desktop Aside */}
            <div className="hidden lg:flex flex-col w-full h-full overflow-y-auto">
                {/* Logo Wrapper */}
                <div className="flex items-center justify-start px-4 gap-4 py-2">
                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden text-2xl cursor-pointer"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle Menu"
                    >
                        <i className="fa fa-bars" aria-hidden="true"></i>
                    </button>

                    {/* Logo */}
                    <div className="flex flex-col items-center">
                        <Link
                            href="https://www.clearpathlending.com/"
                            aria-label="ClearPath Lending"
                            aria-current={pathname === "/" ? "page" : undefined}
                        >
                            <Image
                                src={ClearPathLendingLogo}
                                alt="ClearPath Lending Logo"
                                className="block"
                                width={150}
                                height={50}
                                priority
                            />
                        </Link>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="relative z-[101] mt-[45px]">
                    <ul className="list-none p-1 m-0">
                        {menuItems.map((item, index) => (
                            <li
                                key={item.href}
                                className={`border-b border-lightgray px-4 ${
                                    index === menuItems.length - 1 ? "border-none" : ""
                                }`}
                            >
                                <Link
                                    href={item.href}
                                    className={`flex items-center text-[13px] font-normal uppercase tracking-[0.25px] leading-[40px] transition ${
                                        pathname === item.href ? "text-primary" : "text-textPrimary hover:text-primary"
                                    }`}
                                    aria-current={pathname === item.href ? "page" : undefined}
                                >
                                    <i className={`fa ${item.icon} mr-3`} aria-hidden="true"></i>
                                    <span>{item.label}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>
            </div>
        </aside>
    );
}

export default Aside;
